import { nextTick, onScopeDispose } from 'vue';
import type { FiltersForTable } from '../store/filters.types';
import { useFiltersStore } from '../store/filters.store';
import { useFilters } from './use-filters';

/*
  Bulk operations (clear-all, seeding) dispatch several store actions in the
  same tick: coalesce them into a single notification so consumers refetch
  once instead of once per filter. Vue drops events emitted by unmounted
  components, so call this from a host that stays mounted while filters
  change (e.g. the table itself, not a top bar hidden when the list empties).
*/
export function useOnFiltersChange(
  filterNamespace: string | undefined,
  onChange: (filters?: FiltersForTable) => void,
) {
  if (!filterNamespace) return;

  const filtersStore = useFiltersStore();
  const currentFilterStore = useFilters(filterNamespace);

  let notifyScheduled = false;

  const scheduleNotify = () => {
    if (notifyScheduled) return;
    notifyScheduled = true;
    void nextTick(() => {
      notifyScheduled = false;
      onChange(currentFilterStore.getFiltersForTable.value);
    });
  };

  /*
    The store is shared by every namespace, so the subscription must match the
    namespace carried by the action arguments: without this check a table would
    refetch whenever an unrelated table changed its own filters.
  */
  const unsubscribe = filtersStore.$onAction(({ name, args, after }) => {
    if (name === 'applyFilterValue') {
      if (args[0]?.entityName === filterNamespace) after(scheduleNotify);
      return;
    }
    if (name === 'removeFilter' || name === 'initializeFilter') {
      if (args[0] === filterNamespace) after(scheduleNotify);
    }
  });

  onScopeDispose(() => unsubscribe(), true);

  return unsubscribe;
}
